// Personal vs. entity assignment for account-listing surfaces (MBS, Accounts &
// Net Worth, Quality & Completeness report, and their PDF exports).
//
// Derived from two account properties every account endpoint already returns:
// - `ledger_segment` (+ `ledger_segment_basis`): the categorization ledger
//   (PERSONAL | BUSINESS | INVESTMENT, backend ledger_segment.py). It decides
//   Personal vs Business, so these pages agree with categorization.
// - `title[]` (:TITLED_TO), items { entity_id, display_name, abbreviation,
//   kind, pct }: names WHICH business / trust / estate.
// Single source of the grouping rule so every surface agrees.
//
// Rules (Trello olGOP3Wk, confirmed by product owner 2026-10-10/11):
// - Titled to a Business / Trust / Estate → that entity's group (the backend
//   derives BUSINESS from the same title). A business account belongs to
//   exactly one business, never joint with a person, so a title listing an
//   entity alongside anyone else is a data error: grouped under the entity
//   with the largest pct (ties by name) and flagged `conflict` for review.
//   An investment account follows its title the same way.
// - Exception: a ledger a human set to PERSONAL wins over a BUSINESS title
//   (the segment wins when they disagree); the account is Personal, flagged.
//   Trusts and estates are exempt: they always stay in their own section under
//   "Business & Other Entities" whatever their ledger says — first-class
//   entities, handled like a business in division of property, while the
//   ledger keeps them PERSONAL (product owner, 2026-10-22).
// - No entity on the title but the ledger is BUSINESS (e.g. the statement
//   header says business) → "Business — owner not identified".
// - Everything else, including untitled accounts, is Personal. A sole
//   proprietor recorded as a Person with a DBA name follows its title.
// - Order: Personal first, then entity groups by display name, then the
//   unidentified-business group.

import { formatEntityName } from './formatters';

export const PERSONAL_ASSIGNMENT_KEY = 'personal';
export const PERSONAL_ASSIGNMENT_LABEL = 'Personal';
export const ENTITY_ASSIGNMENTS_LABEL = 'Business & Other Entities';
export const BUSINESS_UNIDENTIFIED_KEY = 'business-unidentified';
export const BUSINESS_UNIDENTIFIED_LABEL = 'Business — owner not identified';

// Why an account is flagged for title review.
export const CONFLICT_MIXED_OWNERS = 'mixed-owners';
export const CONFLICT_PERSONAL_LEDGER = 'personal-ledger';

const LEDGER_PERSONAL = 'PERSONAL';
const LEDGER_BUSINESS = 'BUSINESS';
const BASIS_HUMAN = 'HUMAN';

const PERSONAL_ASSIGNMENT = Object.freeze({
  key: PERSONAL_ASSIGNMENT_KEY,
  label: PERSONAL_ASSIGNMENT_LABEL,
  kind: null,
  entityId: null,
  conflict: false,
  conflictReason: null,
});

const BUSINESS_UNIDENTIFIED_ASSIGNMENT = Object.freeze({
  key: BUSINESS_UNIDENTIFIED_KEY,
  label: BUSINESS_UNIDENTIFIED_LABEL,
  kind: null,
  entityId: null,
  conflict: false,
  conflictReason: null,
});

const isEntityHolder = (item) => !!item?.kind && item.kind !== 'Person';

export function getAccountAssignment(account) {
  const title = Array.isArray(account?.title) ? account.title : [];
  const entities = title.filter(isEntityHolder);
  if (entities.length === 0) {
    return account?.ledger_segment === LEDGER_BUSINESS ? BUSINESS_UNIDENTIFIED_ASSIGNMENT : PERSONAL_ASSIGNMENT;
  }
  const hasBusinessHolder = entities.some((e) => e.kind === 'Business');
  if (hasBusinessHolder && account?.ledger_segment === LEDGER_PERSONAL && account?.ledger_segment_basis === BASIS_HUMAN) {
    return { ...PERSONAL_ASSIGNMENT, conflict: true, conflictReason: CONFLICT_PERSONAL_LEDGER };
  }

  const [chosen] = [...entities].sort((a, b) => {
    const pctDiff = (Number(b.pct) || 0) - (Number(a.pct) || 0);
    if (pctDiff !== 0) return pctDiff;
    return formatEntityName(a).localeCompare(formatEntityName(b));
  });
  return {
    key: `entity:${chosen.entity_id}`,
    label: formatEntityName(chosen),
    kind: chosen.kind,
    entityId: chosen.entity_id,
    conflict: title.length > 1,
    conflictReason: title.length > 1 ? CONFLICT_MIXED_OWNERS : null,
  };
}

// True when at least one account belongs to an entity group. Surfaces only
// render the Personal / entity headers in that case, so all-personal cases look
// exactly as before.
export function hasEntityAssignments(accounts) {
  return (accounts || []).some((a) => getAccountAssignment(a).key !== PERSONAL_ASSIGNMENT_KEY);
}

// Group `items` by assignment, preserving each group's input order.
// `getAccount` maps an item to its account object (identity by default).
// Returns [{ assignment, items }] — Personal first (only if non-empty), then
// entity groups ordered by label.
export function groupByAssignment(items, getAccount = (x) => x) {
  const groups = new Map();
  for (const item of items || []) {
    const assignment = getAccountAssignment(getAccount(item));
    if (!groups.has(assignment.key)) groups.set(assignment.key, { assignment, items: [] });
    groups.get(assignment.key).items.push(item);
  }
  const rank = (key) => {
    if (key === PERSONAL_ASSIGNMENT_KEY) return 0;
    if (key === BUSINESS_UNIDENTIFIED_KEY) return 2;
    return 1;
  };
  return [...groups.values()].sort((a, b) => (
    rank(a.assignment.key) - rank(b.assignment.key)
    || a.assignment.label.localeCompare(b.assignment.label)
  ));
}

// Split an MBS/DRFA section map ({ Category: { accounts: [...] } }) by
// assignment, keeping category order within each group. Associated liabilities
// stay with their parent account. Returns [{ assignment, sections }].
export function groupSectionsByAssignment(sections) {
  const flat = [];
  for (const [sectionName, section] of Object.entries(sections || {})) {
    for (const account of section?.accounts || []) flat.push({ sectionName, account });
  }
  return groupByAssignment(flat, (x) => x.account).map(({ assignment, items }) => {
    const grouped = {};
    for (const { sectionName, account } of items) {
      if (!grouped[sectionName]) grouped[sectionName] = { ...sections[sectionName], accounts: [] };
      grouped[sectionName].accounts.push(account);
    }
    return { assignment, sections: grouped };
  });
}

// MBS layout for one side (assets or liabilities). When `enabled` and at least
// one account is entity-titled, returns one entry per assignment group with a
// `keyPrefix` for line-number / React keys (section names repeat across
// groups). Otherwise returns a single ungrouped entry with an empty prefix so
// callers keep today's layout and keys exactly.
// Returns [{ assignment | null, keyPrefix, sections }].
export function buildAssignmentLayout(sections, { enabled = true } = {}) {
  const accounts = Object.values(sections || {}).flatMap((s) => s?.accounts || []);
  if (!enabled || !hasEntityAssignments(accounts)) {
    return [{ assignment: null, keyPrefix: '', sections: sections || {} }];
  }
  return groupSectionsByAssignment(sections).map((g) => ({ ...g, keyPrefix: `${g.assignment.key}::` }));
}

// Line-number key for an assignment group's subtotal row.
export const assignmentTotalKey = (category, assignment) => `${category}-${assignment.key}-group-total`;
